import { useCallback, useState } from 'react';
// api
import { blogAPI, article_categoryAPI } from 'src/api/blog-api';
// types
import { HTBaiViet, HTBaiVietTheLoai } from 'src/types/htblog';
import { IErrorType } from 'src/types/error';
import { homeAPI } from 'src/api/home-api';
// ----------------------------------------------------------------------

type StatusProps = {
  loading: boolean; 
  empty?: boolean;
  error: IErrorType;
};
 
export default function useBlog() {
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState<HTBaiViet[]>([]);
 
  const [blog, setBlog] = useState<HTBaiViet | null>(null);

  const [latestBlogs, setLatestBlogs] = useState<HTBaiViet[]>([]);

  const [categories, setCategories] = useState<HTBaiVietTheLoai[]>([]);

  const [blogsStatus, setBlogsStatus] = useState<StatusProps>({
    loading: false,
    empty: false,
    error: null,
  });

  const [blogStatus, setBlogStatus] = useState<StatusProps>({
    loading: false,
    error: null,
  });

  const [categoriesStatus, setCategoriesStatus] = useState<StatusProps>({
    loading: false,
    empty: false,
    error: null,
  });

  const handleSetBlogsStatus = useCallback((name: string, value: boolean | null) => {
    setBlogsStatus((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleSetBlogStatus = useCallback((name: string, value: boolean | null) => {
    setBlogStatus((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleSetCategoriesStatus = useCallback((name: string, value: boolean | null) => {
    setCategoriesStatus((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const getBlogs = useCallback(async () => {
    setLoading(true);
    handleSetBlogsStatus('empty', false);
    handleSetBlogsStatus('error', null);
    try {
      const response = await blogAPI.listBaiViet();
      const items = response.items || [];
      setBlogs(items);
      setTimeout(() => {
        setLoading(false); 
      }, 300);
      handleSetBlogsStatus('empty', !items.length);
      handleSetBlogsStatus('error', null);
    } catch (error) {
      console.error(error);
      setLoading(false);
      handleSetBlogsStatus('empty', false);
      handleSetBlogsStatus('error', error);
    }
  }, [handleSetBlogsStatus]);

  const getBlog = useCallback(
    async (id: number) => {
      handleSetBlogStatus('loading', true);
      handleSetBlogStatus('error', null);
      try {
        const response = await blogAPI.getBlogById(id);
        if (response.oneItem) setBlog(response.oneItem);
        handleSetBlogStatus('loading', false);
        handleSetBlogStatus('error', null);
      } catch (error) {
        console.error(error);
        handleSetBlogStatus('loading', false);
        handleSetBlogStatus('error', error);
      }
    },
    [handleSetBlogStatus]
  );
  const getBlogHome = useCallback(
    async (slug: string) => {
      handleSetBlogStatus('loading', true);
      handleSetBlogStatus('error', null);
      try {
        const response = await homeAPI.getBlogHome(slug);
        if (response.oneItem) setBlog(response.oneItem);
        handleSetBlogStatus('loading', false); 
        handleSetBlogStatus('error', null);
      } catch (error) {
        console.error(error);
        handleSetBlogStatus('loading', false);
        handleSetBlogStatus('error', error); 
      }
    },
    [handleSetBlogStatus]
  );
  const getLatestBlogs = useCallback(async () => {
    try {
      const response = await blogAPI.listBaiViet();
      const items = response.items || [];
      const sorted = items
        .slice()
        .sort((a, b) => new Date(b.ngayTao).getTime() - new Date(a.ngayTao).getTime())
        .slice(0, 5);
      setLatestBlogs(sorted);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const getCategories = useCallback(async () => {
    handleSetCategoriesStatus('loading', true);
    handleSetCategoriesStatus('empty', false);
    handleSetCategoriesStatus('error', null);
    try {
      setLoading(true);
      const response = await article_categoryAPI.listBaiVietTheLoai();
      const items = response.items || [];
      setCategories(items);
      setTimeout(() => {
        setLoading(false);
      }, 300);
      handleSetCategoriesStatus('loading', false);
      handleSetCategoriesStatus('empty', !items.length);
      handleSetCategoriesStatus('error', null);
    } catch (error) {
      console.error(error);
      handleSetCategoriesStatus('loading', false);
      handleSetCategoriesStatus('empty', false);
      handleSetCategoriesStatus('error', error);
      setLoading(false);
    }
  }, [handleSetCategoriesStatus]);

  const deleteBlog = useCallback(async (ids: number | number[]) => {
    try {
      const response = await blogAPI.deleteBlog(ids);
      if (response?.messCode === 1) {
        const idArray = Array.isArray(ids) ? ids : [ids];
        setBlogs(prev => prev.filter((b) => !idArray.includes(b.id)));
        setLatestBlogs(prev => prev.filter((b) => !idArray.includes(b.id))); 
        if (blog && idArray.includes(blog.id)) {
          setBlog(null);
        }
        return true;
      }
      throw new Error(response?.message || 'Xóa bài viết thất bại');
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [blog]);

  return {
    loading,
    blog,
    blogs,
    latestBlogs,
    categories,
    //
    blogsStatus,
    blogStatus,
    categoriesStatus,
    //
    getBlog,
    getBlogHome,
    getBlogs,
    getLatestBlogs,
    getCategories,
    deleteBlog,
  };
}
